/**
 * The payment step: payment method, shipping set and what the two allow together.
 *
 * Markup contract:
 *   <div class="checkout_main" x-data="paymentStep({ matrix: {…}, paymentId: '…', shipSet: '…', totalZero: false })">
 *     <form id="payment" x-data="validatedForm" …>
 *       <input type="radio" name="paymentid" value="…" x-model="paymentId" @change="pickPayment($el)">
 *       <input type="radio" name="sShipSet" value="…" x-model="shipSet" @change="pickSet()"
 *              :disabled="!isSetAllowed('…')">
 *     <div id="crdBasketBox" x-data="stickyBasket({…})">
 *
 * The matrix comes from the server (payment method id => the shipping sets it allows);
 * nothing else decides which combination is valid. Every pick is an action that
 * re-renders the sticky basket, so no price is ever calculated in the browser.
 */
import { stickyBasketAction } from './stickyBasket.js';
import { clearRegistrationState } from '../forms/registrationState.js';

// Cash on delivery: the parcel is paid at the door, so neither the express set
// (same day) nor delivery to a store can carry it (CHEC-348).
const CASH_ON_DELIVERY = ['oxidcashondel', 'oxidcashondel_ups'];
const DHL_EXPRESS = 'f9aa94293617ca89bc10683a21463de3';
const HIDDEN_FOR_CASH_ON_DELIVERY = [DHL_EXPRESS, 'dd_filial-lieferung'];

document.addEventListener('alpine:init', () => {
    Alpine.data('paymentStep', ({
        matrix = {}, paymentId = '', shipSet = '', totalZero = false, storeSet = '', storeUrl = '',
    }) => ({
        paymentId,
        shipSet,
        isSelecting: false,
        selectionPromise: null,
        selectionRevision: 0,

        init() {
            // from this step on the customer has an account, the registration draft is stale
            clearRegistrationState();
            Alpine.effect(() => this.applyTooltips());
        },

        isSetAllowed(id) {
            return (matrix[this.paymentId] || []).includes(id);
        },

        get isCashOnDelivery() {
            return CASH_ON_DELIVERY.includes(this.paymentId);
        },

        isSetVisible(id) {
            return !(this.isCashOnDelivery && HIDDEN_FOR_CASH_ON_DELIVERY.includes(id));
        },

        get selectionValid() {
            return this.paymentId !== '' && this.isSetAllowed(this.shipSet);
        },

        pickPayment(radio) {
            if (!this.isSetAllowed(this.shipSet)) {
                this.shipSet = '';
            }

            radio.closest('.crdPay_item').scrollIntoView({ block: 'start', behavior: 'smooth' });

            return this.select();
        },

        pickSet() {
            return this.select();
        },

        /** The store set is only offered once a store is picked, which happens in the address step. */
        async chooseStore() {
            if (!this.isSetAllowed(storeSet)) {
                return;
            }

            this.shipSet = storeSet;
            await this.select();
            window.location.href = storeUrl;
        },

        select() {
            if (this.isCashOnDelivery || this.shipSet === DHL_EXPRESS) {
                document.getElementById('desiredshippingdate').value = '';
            }

            this.isSelecting = true;
            this.selectionRevision++;
            const paymentid = this.paymentId;
            const sShipSet = this.shipSet;
            // Keep the server selection in click order; payment hooks await the latest update.
            const update = (this.selectionPromise || Promise.resolve()).catch(() => {}).then(async () => {
                await stickyBasketAction('setSelection', {
                    paymentid,
                    sShipSet,
                    parentClass: 'payment',
                });

                // a basket that turns free — or stops being free — offers other payment methods
                const wrapper = document.querySelector('#crdBasketBox .basketBoxWrapper');
                if ((wrapper.dataset.totalZero === '1') !== totalZero) {
                    window.location.reload();
                }
            }).finally(() => {
                if (this.selectionPromise === update) this.isSelecting = false;
            });
            this.selectionPromise = update;
            return update;
        },

        /** Only a set the chosen payment method forbids explains itself, the others stay silent. */
        applyTooltips() {
            document.querySelectorAll('#crdDel .crdDel_item').forEach((item) => {
                const head = item.querySelector('.acc_head');
                const radio = item.querySelector('input[name="sShipSet"]');
                const tooltip = window.bootstrap.Tooltip.getOrCreateInstance(head);

                tooltip.hide();
                if (this.isSetAllowed(radio.value)) {
                    tooltip.disable();
                    return;
                }
                tooltip.enable();
            });
        },
    }));
});
